// StackedCountChart.tsx
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from 'recharts';
import { axisMaxByMSD, makeStack, toNum } from './chartUtils';

type Props = {
  data: any[];
  height?: number;
  tickWidth?: number;
  leftAxisWidth?: number;
  title?: string;
};

const H_DEFAULT = 320;
const TICK_W_DEFAULT = 56;
const LEFT_W_DEFAULT = 50;

// legendLabel별 이미지 매핑
const legendImageMap: Record<string, string> = {
  진행건수: '/assets/images/icon/gray_box.svg',
  낙찰건수: '/assets/images/icon/dark_gray_box.svg',
};

function CustomBarTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;

  const won = payload.find((p) => p.dataKey === 'wonCountCapped');
  const remainder = payload.find((p) => p.dataKey === 'progressRemainder');

  const progress = Number(remainder?.payload?.progress ?? remainder?.value ?? 0);
  const wonCount = Number(won?.value ?? 0);

  return (
    <TooltipContainer>
      <TooltipContent>
        <TooltipLabel>{label}</TooltipLabel>
        <TooltipRow>
          <TooltipLegendWrapper>
            <TooltipImage src={legendImageMap['진행건수']} alt="진행건수" />
            <TooltipLegend>진행건수</TooltipLegend>
          </TooltipLegendWrapper>
          <TooltipValue>{progress.toLocaleString('ko-KR')}건</TooltipValue>
        </TooltipRow>
        <TooltipRow>
          <TooltipLegendWrapper>
            <TooltipImage src={legendImageMap['낙찰건수']} alt="낙찰건수" />
            <TooltipLegend>낙찰건수</TooltipLegend>
          </TooltipLegendWrapper>
          <TooltipValue>{wonCount.toLocaleString('ko-KR')}건</TooltipValue>
        </TooltipRow>
      </TooltipContent>
    </TooltipContainer>
  );
}

function StackedCountChartBase({
  data,
  height = H_DEFAULT,
  tickWidth = TICK_W_DEFAULT,
  leftAxisWidth = LEFT_W_DEFAULT,
}: Props) {
  const chartData = useMemo(() => makeStack(data), [data]);
  const countAxisMax = useMemo(
    () => axisMaxByMSD(Math.max(0, ...data.map((d) => toNum(d.progress)))),
    [data],
  );
  const innerWidth = Math.max(tickWidth * data.length, 480);

  return (
    <Wrap data-capture-section>
      <Grid style={{ gridTemplateColumns: `${leftAxisWidth}px minmax(0,1fr)` }}>
        {/* 좌측 축만 */}
        <AxisPane>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={chartData}
              margin={{ top: 22, right: 10, left: -10, bottom: 38 }}
            >
              <XAxis dataKey="label" hide />
              <YAxis
                domain={[0, countAxisMax]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                axisLine
                tickLine={{ stroke: '#787D87' }}
                tickFormatter={(v) => Number(v).toLocaleString()}
              >
                <Label
                  value="건수"
                  position="insideTopLeft"
                  offset={30}
                  dy={-50}
                  style={{
                    fill: '#787D87',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>

        {/* 가운데 플롯 + X축 (가로 스크롤) */}
        <ScrollPane data-export-expand>
          <Inner style={{ width: innerWidth, height }} data-export-inner>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 16, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine
                  tickLine={{ stroke: '#787D87' }}
                />
                <YAxis hide domain={[0, countAxisMax]} />
                <Tooltip
                  content={({ active, payload, label: tooltipLabel }) => (
                    <CustomBarTooltip
                      active={active}
                      payload={payload}
                      label={tooltipLabel}
                    />
                  )}
                />
                <Bar
                  yAxisId={0}
                  dataKey="wonCountCapped"
                  name="낙찰건수"
                  stackId="cnt"
                  fill="#424854"
                  barSize={48}
                  radius={[0, 0, 6, 6]}
                />
                <Bar
                  yAxisId={0}
                  dataKey="progressRemainder"
                  name=""
                  stackId="cnt"
                  fill="#D3D8E0"
                  barSize={48}
                  radius={[6, 6, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
      </Grid>
    </Wrap>
  );
}

export default memo(StackedCountChartBase);

const Wrap = styled.div``;
const Grid = styled.div`
  display: grid;
  align-items: start;
  background: #fff;
`;
const AxisPane = styled.div``;
const ScrollPane = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Inner = styled.div`
  padding-bottom: 8px;
`;

const TooltipContainer = styled.div`
  display: flex;
  width: 200px;
  padding: 20px 16px;
  background: #fff;
  border-radius: 8px;
  opacity: 1;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  align-items: center;
  justify-content: center;
`;
const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;
const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
`;
const TooltipLegendWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const TooltipLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  font-style: normal;
  line-height: 24px;
  letter-spacing: -0.02em;
  color: #333;
`;
const TooltipImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 4px;
`;
const TooltipLegend = styled.div`
  font-weight: 500;
  font-style: Medium;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  color: #424854;
`;
const TooltipValue = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -2%;
`;
